import { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import {
  Calendar,
  Users,
  UserPlus,
  Car,
  Plus,
  X,
  Send,
  Clock,
  MapPinned,
  UserCheck,
  LogOut,
  Loader2,
  Trash2,
  Pencil,
} from "lucide-react";

const EMPTY_RIDE = {
  ride_type: "offering",
  destination: "",
  date: "",
  time: "09:00",
  seats: 1,
  notes: "",
  return_trip: false,
  return_time: ""
};

const RidesTab = ({ user, rides, headers, onRefresh }) => {
  const [showRideForm, setShowRideForm] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [editingRideId, setEditingRideId] = useState(null);
  const [newRide, setNewRide] = useState(EMPTY_RIDE);
  const [submittingRide, setSubmittingRide] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const isAdmin = user?.role === "admin" || user?.is_admin;

  const submitRide = async () => {
    if (!newRide.destination || !newRide.date || !newRide.time) {
      toast.error("Please fill in destination, date and time");
      return;
    }

    setSubmittingRide(true);
    try {
      if (editingRideId) {
        await axios.put(`${API}/rides/${editingRideId}`, newRide, { headers });
        toast.success("Ride updated ✓");
      } else {
        await axios.post(`${API}/rides`, newRide, { headers });
        toast.success(newRide.ride_type === "offering" ? "Ride offered! 🚗" : "Ride request posted!");
      }
      setShowRideForm(false);
      setEditingRideId(null);
      setNewRide(EMPTY_RIDE);
      setSelectedRide(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't save ride");
    } finally {
      setSubmittingRide(false);
    }
  };

  const startEditingRide = (ride) => {
    setNewRide({
      ride_type: ride.ride_type,
      destination: ride.destination,
      date: ride.date,
      time: ride.time,
      seats: ride.seats || 1,
      notes: ride.notes || "",
      return_trip: !!ride.return_trip,
      return_time: ride.return_time || "",
    });
    setEditingRideId(ride.id);
    setSelectedRide(null);
    setShowRideForm(true);
  };

  const joinRide = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/join`, { seats_needed: 1 }, { headers });
      toast.success("You've joined the ride! 🚗");
      onRefresh();
      setSelectedRide(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't join ride");
    }
  };

  const leaveRide = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/leave`, {}, { headers });
      toast.success("Left the ride");
      onRefresh();
      setSelectedRide(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't leave ride");
    }
  };

  const deleteRide = async (rideId) => {
    try {
      await axios.delete(`${API}/rides/${rideId}`, { headers });
      toast.success("Ride cancelled");
      onRefresh();
      setSelectedRide(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't delete ride");
    }
  };

  const sendRideMessage = async (rideId) => {
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      await axios.post(`${API}/rides/${rideId}/messages`, { message: newMessage }, { headers });
      setNewMessage("");
      // Refresh the selected ride details
      const res = await axios.get(`${API}/rides/${rideId}`, { headers });
      setSelectedRide(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const isRideParticipant = (ride) => {
    return ride.participants?.some(p => p.user_id === user?.id);
  };

  const isRideCreator = (ride) => {
    return ride.created_by_id === user?.id;
  };

  const formatRideDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const getAvailableSeats = (ride) => {
    if (ride.ride_type !== "offering") return null;
    const taken = ride.participants?.reduce((sum, p) => sum + (p.seats || 1), 0) || 0;
    return ride.seats - taken;
  };

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#C85A38] to-[#C96A50] rounded-xl p-5 text-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-serif text-xl">Rideshare</h3>
            <p className="text-white/80 text-sm">Share rides with fellow guests</p>
          </div>
          <button
            onClick={() => setShowRideForm(true)}
            className="bg-white/20 hover:bg-white/30 p-3 rounded-full transition-colors"
            data-testid="add-ride-btn"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{rides.filter(r => r.ride_type === "offering").length}</p>
            <p className="text-xs text-white/80">Rides Offered</p>
          </div>
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{rides.filter(r => r.ride_type === "requesting").length}</p>
            <p className="text-xs text-white/80">Rides Requested</p>
          </div>
        </div>
      </div>

      {/* Ride Create Form */}
      {showRideForm && (
        <div className="bg-white rounded-xl p-5 border border-stone-100 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-serif text-lg text-[#1A1A1A]">
              {editingRideId
                ? "Edit Ride"
                : (newRide.ride_type === "offering" ? "Offer a Ride" : "Request a Ride")}
            </h4>
            <button
              onClick={() => { setShowRideForm(false); setEditingRideId(null); setNewRide(EMPTY_RIDE); }}
              className="p-2 hover:bg-stone-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-[#1A1A1A]/60" />
            </button>
          </div>

          {/* Ride Type Toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setNewRide({ ...newRide, ride_type: "offering" })}
              className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                newRide.ride_type === "offering"
                  ? "bg-[#8C9A7C] text-white"
                  : "bg-stone-100 text-[#1A1A1A]/60"
              }`}
            >
              🚗 I'm Offering
            </button>
            <button
              onClick={() => setNewRide({ ...newRide, ride_type: "requesting" })}
              className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                newRide.ride_type === "requesting"
                  ? "bg-[#C85A38] text-white"
                  : "bg-stone-100 text-[#1A1A1A]/60"
              }`}
            >
              🙋 I Need a Ride
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#1A1A1A]/60 mb-1 block">Destination *</label>
              <input
                type="text"
                value={newRide.destination}
                onChange={(e) => setNewRide({ ...newRide, destination: e.target.value })}
                placeholder="e.g., Barcelona Airport, Sitges Beach, IKEA..."
                className="w-full p-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
                data-testid="ride-destination-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-[#1A1A1A]/60 mb-1 block">Date *</label>
                <input
                  type="date"
                  value={newRide.date}
                  onChange={(e) => setNewRide({ ...newRide, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full p-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
                  data-testid="ride-date-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#1A1A1A]/60 mb-1 block">Time *</label>
                <input
                  type="time"
                  value={newRide.time}
                  onChange={(e) => setNewRide({ ...newRide, time: e.target.value })}
                  className="w-full p-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
                  data-testid="ride-time-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-[#1A1A1A]/60 mb-1 block">
                {newRide.ride_type === "offering" ? "Available Seats" : "Seats Needed"}
              </label>
              <select
                value={newRide.seats}
                onChange={(e) => setNewRide({ ...newRide, seats: parseInt(e.target.value) })}
                className="w-full p-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
              >
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <option key={n} value={n}>{n} seat{n > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="returnTrip"
                checked={newRide.return_trip}
                onChange={(e) => setNewRide({ ...newRide, return_trip: e.target.checked })}
                className="w-5 h-5 rounded border-stone-300 text-[#C85A38] focus:ring-[#C85A38]"
              />
              <label htmlFor="returnTrip" className="text-sm text-[#1A1A1A]">
                Return trip available
              </label>
            </div>

            {newRide.return_trip && (
              <div>
                <label className="text-sm text-[#1A1A1A]/60 mb-1 block">Return Time</label>
                <input
                  type="time"
                  value={newRide.return_time}
                  onChange={(e) => setNewRide({ ...newRide, return_time: e.target.value })}
                  className="w-full p-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
                />
              </div>
            )}

            <div>
              <label className="text-sm text-[#1A1A1A]/60 mb-1 block">Notes (optional)</label>
              <textarea
                value={newRide.notes}
                onChange={(e) => setNewRide({ ...newRide, notes: e.target.value })}
                placeholder="Any details about the trip, meeting point, luggage space, etc."
                rows={2}
                className="w-full p-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30 resize-none"
              />
            </div>

            <Button
              onClick={submitRide}
              disabled={submittingRide}
              className="w-full py-3 rounded-xl bg-[#C85A38] hover:bg-[#C96A50] text-white"
              data-testid="submit-ride-btn"
            >
              {submittingRide ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                editingRideId
                  ? "Save changes"
                  : (newRide.ride_type === "offering" ? "Post Ride Offer" : "Post Ride Request")
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Rides Offered */}
      <div className="bg-white rounded-xl p-5 border border-stone-100">
        <h4 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
          <Car className="w-5 h-5 text-[#8C9A7C]" />
          Rides Being Offered
        </h4>
        
        {rides.filter(r => r.ride_type === "offering").length > 0 ? (
          <div className="space-y-3">
            {rides.filter(r => r.ride_type === "offering").map((ride) => (
              <div
                key={ride.id}
                onClick={() => setSelectedRide(ride)}
                className="bg-[#F0EDE3] rounded-2xl p-4 cursor-pointer hover:bg-[#E8E4D0] transition-colors"
                data-testid={`ride-card-${ride.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden">
                      {ride.created_by_avatar ? (
                        <img src={ride.created_by_avatar} alt={ride.created_by_name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-[#1A1A1A] font-medium">{ride.created_by_name?.charAt(0)}</span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-[#1A1A1A]">{ride.created_by_name}</p>
                      <p className="text-xs text-[#1A1A1A]/60">is offering a ride</p>
                    </div>
                  </div>
                  {ride.status === "full" && (
                    <span className="text-xs bg-orange-500 text-white px-2 py-1 rounded-full">Full</span>
                  )}
                </div>
                
                <div className="bg-white rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <MapPinned className="w-4 h-4 text-[#C85A38]" />
                    <span className="text-sm font-medium text-[#1A1A1A]">{ride.destination}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[#1A1A1A]/60">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatRideDate(ride.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {ride.time}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {getAvailableSeats(ride)} seat{getAvailableSeats(ride) !== 1 ? 's' : ''} left
                    </span>
                  </div>
                  {ride.return_trip && (
                    <p className="text-xs text-[#8C9A7C]">↩ Return trip at {ride.return_time}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-[#1A1A1A]/50">
            <Car className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No rides being offered right now</p>
          </div>
        )}
      </div>

      {/* Rides Requested */}
      <div className="bg-white rounded-xl p-5 border border-stone-100">
        <h4 className="font-medium text-[#1A1A1A] mb-4 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#C85A38]" />
          Ride Requests
        </h4>
        
        {rides.filter(r => r.ride_type === "requesting").length > 0 ? (
          <div className="space-y-3">
            {rides.filter(r => r.ride_type === "requesting").map((ride) => (
              <div
                key={ride.id}
                onClick={() => setSelectedRide(ride)}
                className="bg-[#FEF3F0] rounded-2xl p-4 cursor-pointer hover:bg-[#FDEAE4] transition-colors"
                data-testid={`ride-request-${ride.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden">
                      {ride.created_by_avatar ? (
                        <img src={ride.created_by_avatar} alt={ride.created_by_name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-[#1A1A1A] font-medium">{ride.created_by_name?.charAt(0)}</span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-[#1A1A1A]">{ride.created_by_name}</p>
                      <p className="text-xs text-[#C85A38]">needs a ride</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <MapPinned className="w-4 h-4 text-[#C85A38]" />
                    <span className="text-sm font-medium text-[#1A1A1A]">{ride.destination}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[#1A1A1A]/60">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatRideDate(ride.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {ride.time}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {ride.seats} seat{ride.seats !== 1 ? 's' : ''} needed
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-[#1A1A1A]/50">
            <UserPlus className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No ride requests right now</p>
          </div>
        )}
      </div>
    </div>

    {/* Ride Detail Modal — portaled to escape any ancestor transform (e.g. .animate-fade-in) */}
    {selectedRide && createPortal((
      <div className="fixed inset-0 bg-black/50 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 pb-[calc(72px+env(safe-area-inset-bottom))] sm:pb-4">
        <div className="bg-white rounded-t-3xl sm:rounded-xl w-full sm:max-w-md max-h-[85vh] overflow-hidden shadow-xl animate-slide-up">
          {/* Header */}
          <div className={`p-5 ${selectedRide.ride_type === "offering" ? "bg-gradient-to-br from-[#8C9A7C] to-[#7C8A6C]" : "bg-gradient-to-br from-[#C85A38] to-[#C96A50]"} text-white`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                  {selectedRide.created_by_avatar ? (
                    <img src={selectedRide.created_by_avatar} alt={selectedRide.created_by_name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-medium text-lg">{selectedRide.created_by_name?.charAt(0)}</span>
                  )}
                </div>
                <div>
                  <p className="font-medium">{selectedRide.created_by_name}</p>
                  <p className="text-white/80 text-sm">
                    {selectedRide.ride_type === "offering" ? "is offering a ride" : "needs a ride"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedRide(null)}
                className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="bg-white/20 rounded-2xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                <MapPinned className="w-5 h-5" />
                <span className="font-medium">{selectedRide.destination}</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-white/80">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatRideDate(selectedRide.date)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {selectedRide.time}
                </span>
              </div>
              {selectedRide.return_trip && (
                <p className="text-sm">↩ Return at {selectedRide.return_time}</p>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-5 space-y-4 overflow-y-auto max-h-[50vh]">
            {/* Notes */}
            {selectedRide.notes && (
              <div className="bg-[#F0EDE3] rounded-xl p-3">
                <p className="text-sm text-[#1A1A1A]">{selectedRide.notes}</p>
              </div>
            )}

            {/* Seats Info */}
            <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
              <Users className="w-5 h-5 text-[#1A1A1A]/60" />
              {selectedRide.ride_type === "offering" ? (
                <span className="text-sm text-[#1A1A1A]">
                  {getAvailableSeats(selectedRide)} of {selectedRide.seats} seats available
                </span>
              ) : (
                <span className="text-sm text-[#1A1A1A]">
                  Looking for {selectedRide.seats} seat{selectedRide.seats !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Participants */}
            {selectedRide.participants?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-[#1A1A1A] mb-2">
                  {selectedRide.ride_type === "offering" ? "Passengers" : "Drivers interested"}
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedRide.participants.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-[#F0EDE3] rounded-full px-3 py-1.5">
                      <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center overflow-hidden text-xs">
                        {p.user_avatar ? (
                          <img src={p.user_avatar} alt={p.user_name} className="w-full h-full object-cover" />
                        ) : (
                          <span>{p.user_name?.charAt(0)}</span>
                        )}
                      </div>
                      <span className="text-sm text-[#1A1A1A]">{p.user_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {(isRideCreator(selectedRide) || isRideParticipant(selectedRide)) && (
              <div>
                <p className="text-sm font-medium text-[#1A1A1A] mb-2">Messages</p>
                <div className="bg-stone-50 rounded-xl p-3 space-y-3 max-h-40 overflow-y-auto">
                  {selectedRide.messages?.length > 0 ? (
                    selectedRide.messages.map((msg, idx) => (
                      <div key={idx} className={`flex gap-2 ${msg.user_id === user?.id ? "flex-row-reverse" : ""}`}>
                        <div className="w-6 h-6 rounded-full bg-[#C85A38]/20 flex items-center justify-center text-xs flex-shrink-0">
                          {msg.user_name?.charAt(0)}
                        </div>
                        <div className={`max-w-[75%] rounded-xl px-3 py-2 ${msg.user_id === user?.id ? "bg-[#C85A38] text-white" : "bg-white"}`}>
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-[10px] mt-1 ${msg.user_id === user?.id ? "text-white/70" : "text-[#1A1A1A]/50"}`}>
                            {new Date(msg.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-[#1A1A1A]/50 text-center py-4">No messages yet</p>
                  )}
                </div>
                
                {/* Message Input */}
                <div className="flex gap-2 mt-3">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 p-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
                    onKeyDown={(e) => e.key === "Enter" && sendRideMessage(selectedRide.id)}
                  />
                  <Button
                    onClick={() => sendRideMessage(selectedRide.id)}
                    disabled={sendingMessage || !newMessage.trim()}
                    className="px-4 rounded-xl bg-[#C85A38] hover:bg-[#C96A50] text-white"
                  >
                    {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-5 border-t border-stone-100 space-y-3">
            {/* Admin override / creator controls — always available to admin or creator */}
            {(isAdmin || isRideCreator(selectedRide)) && (
              <div className="grid grid-cols-2 gap-2" data-testid="ride-owner-actions">
                <Button
                  onClick={() => startEditingRide(selectedRide)}
                  variant="outline"
                  className="py-3 rounded-xl border-stone-200 text-[#1A1A1A] hover:bg-stone-50"
                  data-testid="edit-ride-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  onClick={() => deleteRide(selectedRide.id)}
                  variant="outline"
                  className="py-3 rounded-xl border-red-200 text-red-500 hover:bg-red-50"
                  data-testid="delete-ride-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {isRideCreator(selectedRide) ? "Cancel" : "Delete (admin)"}
                </Button>
              </div>
            )}

            {/* Participant / join actions — only for non-creators on active rides */}
            {!isRideCreator(selectedRide) && (
              isRideParticipant(selectedRide) ? (
                <Button
                  onClick={() => leaveRide(selectedRide.id)}
                  variant="outline"
                  className="w-full py-3 rounded-xl border-orange-200 text-orange-500 hover:bg-orange-50"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Leave This Ride
                </Button>
              ) : selectedRide.status === "active" ? (
                <Button
                  onClick={() => joinRide(selectedRide.id)}
                  className="w-full py-3 rounded-xl bg-[#8C9A7C] hover:bg-[#7C8A6C] text-white"
                >
                  <UserCheck className="w-4 h-4 mr-2" />
                  {selectedRide.ride_type === "offering" ? "Join This Ride" : "I Can Help!"}
                </Button>
              ) : (
                <div className="text-center py-3 text-[#1A1A1A]/60">
                  This ride is no longer accepting passengers
                </div>
              )
            )}
          </div>
        </div>
      </div>
    ), document.body)}
    </>
  );
};

export default RidesTab;
